import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { PointmanCompany, PointmanCustomerDetailDTO, PointmanCustomerParams } from '_lib/api/apiTypes.shim';

import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';

import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { useAlertDialog } from '_lib/hooks/AlertDialogHook';

import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';

import { selectPhoneCallContext, setPartialPhoneCallContext } from '../../PointmanPhoneCall.slice';

import { CustomerDetail } from './CustomerDetail/CustomerDetail';
import { CustomerSelection } from './CustomerSelection/CustomerSelection';

type CustomerPaneProps = {
  phoneCallURLState: PhoneCallURLState;
  company: PointmanCompany;
  onSetIsSaving: (isSaving: boolean) => void;
};

export const PointmanCustomerPane = (props: CustomerPaneProps) => {
  const { phoneCallURLState, company, onSetIsSaving } = props;

  const alertDialog = useAlertDialog();

  const api = useContext(ApiContext);

  const [customerSearchWords, setCustomerSearchWords] = useState<PointmanCustomerParams['searchWords']>(
    phoneCallURLState.callerId || ''
  );
  const customerQuery = api.pointmanCustomers.useList(
    {
      params: { searchWords: customerSearchWords, companyId: company.id }
    },
    {
      enabled: customerSearchWords !== undefined && customerSearchWords.length >= 2
    }
  );
  const { customer } = useAppSelector(selectPhoneCallContext);
  const dispatch = useAppDispatch();

  const handleSetCustomer = useCallback(
    async (customer: PointmanCustomerDetailDTO | null) => {
      dispatch(
        setPartialPhoneCallContext({
          updates: { customer }
        })
      );

      if (customer && customer.zipOrPostal === null) {
        await alertDialog.show({
          variant: 'warning',
          titleText: 'NO ZIP CODE FOR CUSTOMER',
          content: company.isZoned ? 'Please verify zip code and select zone' : 'Please verify zip code'
        });
      }
    },
    [alertDialog, company.isZoned, dispatch]
  );

  const firstFetch = useRef(true);
  useEffect(() => {
    const setSingleCustomer = async (customerId: string) => {
      onSetIsSaving(true);
      try {
        const customerDetail = await api.pointmanCustomers.getById({
          id: customerId,
          params: { companyId: company.id }
        });
        handleSetCustomer(customerDetail);
      } finally {
        onSetIsSaving(false);
      }
    };

    if (firstFetch.current && customerQuery.data) {
      firstFetch.current = false;
      if (
        customerQuery.data.pages.length > 0 &&
        customerQuery.data.pages[0] &&
        customerQuery.data.pages[0].results &&
        customerQuery.data.pages[0].results.length === 1 &&
        customerQuery.data.pages[0].results[0]
      ) {
        const customerId = customerQuery.data.pages[0].results[0].id;
        setSingleCustomer(customerId);
      }
    }
  }, [api.pointmanCustomers, company.id, customerQuery.data, handleSetCustomer, onSetIsSaving]);

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('pane', 'Customer Location Pane');
      }}
      fallback={({ resetError }) => (
        <ErrorBoundaryFallback
          title='Error Displaying Customers'
          onRefresh={() => {
            resetError();
          }}
        />
      )}>
      <FlexBox
        sx={(theme) => ({
          height: '100%',
          width: '100%',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default
        })}>
        {customer && (
          <CustomerDetail
            customer={customer}
            callerId={phoneCallURLState.callerId}
            company={company}
            onSetCustomer={(customer) => handleSetCustomer(customer)}
          />
        )}
        {!customer && (
          <CustomerSelection
            callerId={phoneCallURLState.callerId}
            companyId={company.id}
            customerQuery={customerQuery}
            customerSearchWords={customerSearchWords}
            setCustomerSearchWords={setCustomerSearchWords}
            onSetCustomer={(customer) => handleSetCustomer(customer)}
          />
        )}
      </FlexBox>
      {alertDialog.element}
    </ErrorBoundary>
  );
};
